/**
 * Central registry for per-partner feature flags.
 *
 * Each entry here is a toggle an admin can enable/disable on a single partner
 * via the "Features" modal on `partnerAdministrationList.vue`. Menu items and
 * other UI elements gate their visibility on these keys via
 * `requiresPartnerFeature` (see `@/constants/menu/types`).
 *
 * To add a new feature toggle:
 * 1. Add a key constant to `PARTNER_FEATURE_FLAGS`.
 * 2. Add a matching entry to `PARTNER_FEATURE_DEFINITIONS` with a
 *    human-readable label and description.
 * 3. Tag the relevant menu item(s) with
 *    `requiresPartnerFeature: PARTNER_FEATURE_FLAGS.<KEY>`.
 */
export const PARTNER_FEATURE_FLAGS = {
  SAVINGS_CALCULATOR: 'savingsCalculatorEnabled',
  EXPORT_CENTER: 'exportCenterEnabled',
  DOCUMENTS: 'showDocuments',
  DRIVER_ACCOUNT_MANAGEMENT: 'driverAccountManagementEnabled',
  AI_AT_RYD: 'aiAtRydEnabled',
  AI_CONNECTOR: 'aiConnectorEnabled',
  AI_WRITE_ACTIONS: 'aiWriteActionsEnabled',
  SECURITY_AND_COMPLIANCE: 'securityAndComplianceEnabled',
} as const;

export type PartnerFeatureFlagKey =
  (typeof PARTNER_FEATURE_FLAGS)[keyof typeof PARTNER_FEATURE_FLAGS];

export interface PartnerFeatureDefinition {
  key: PartnerFeatureFlagKey;
  label: string;
  description: string;
}

export const PARTNER_FEATURE_DEFINITIONS: PartnerFeatureDefinition[] = [
  {
    key: PARTNER_FEATURE_FLAGS.SAVINGS_CALCULATOR,
    label: 'Savings Calculator',
    description: 'Shows the "Savings Calculator" menu under Fleet Management for this partner.',
  },
  {
    key: PARTNER_FEATURE_FLAGS.EXPORT_CENTER,
    label: 'Export Center',
    description:
      'Shows the "Export Center" menu for a partner without a paid fleet plan. Paid plans ' +
      'always see it, so this is only needed to keep access after a downgrade.',
  },
  {
    key: PARTNER_FEATURE_FLAGS.DOCUMENTS,
    label: 'Show Documents',
    description:
      'Shows the top-level "Documents" page where this partner can view files shared by ryd.',
  },
  {
    key: PARTNER_FEATURE_FLAGS.DRIVER_ACCOUNT_MANAGEMENT,
    label: 'Driver / Account Management',
    description:
      'Shows the "My Fleet → Drivers / Accounts" pages where this API partner lists, creates and ' +
      'updates drivers (with their external driver / company IDs) across its fleet and sub-fleets, ' +
      'and the partner-system interactions on order details.',
  },
  {
    key: PARTNER_FEATURE_FLAGS.AI_AT_RYD,
    label: 'AI at ryd',
    description:
      'Shows the "AI at ryd" section: the ways this partner can work with ryd through AI (portal ' +
      'assistant, own AI assistant via MCP, automations, APIs, agents), how to connect each ' +
      'platform, and the security notes. Documentation only — it does not grant AI data access.',
  },
  {
    key: PARTNER_FEATURE_FLAGS.AI_CONNECTOR,
    label: 'AI connector (own AI assistants)',
    description:
      "Lets this partner's users connect their own AI assistants (Claude, ChatGPT, Microsoft Copilot, " +
      'n8n) to ryd through the MCP server and read data with their portal rights. Required for any AI ' +
      'data access; changes additionally need AI write actions.',
  },
  {
    key: PARTNER_FEATURE_FLAGS.AI_WRITE_ACTIONS,
    label: 'AI write actions',
    description:
      "Lets this partner's AI assistants change data through the ryd MCP server: create, update " +
      'and archive drivers, add and update vehicles, assign vehicles, and update webhook ' +
      'configurations. Each change still needs the write permission at connect, the matching ' +
      'portal role and the user’s confirmation of a preview; every change is activity logged.',
  },
  {
    key: PARTNER_FEATURE_FLAGS.SECURITY_AND_COMPLIANCE,
    label: 'Security & Compliance (fleet partners)',
    description:
      'Shows the "Security & Compliance" section (ryd IP addresses to allowlist, data protection ' +
      'and GDPR) to a fleet partner. Every other partner type always sees it; only users whose ' +
      'sole partner type is Fleet Partner need this switch.',
  },
];

/**
 * API access capability switches shown in the same "Partner Features" modal,
 * but NOT part of the `featureFlags` map: each key is a top-level field on the
 * partner-config document and is saved via the generic partner-config update
 * endpoint. They gate API credentials, documentation and whole menu sections
 * (`requiresFleetApis` / `hiddenForFleetApis`).
 */
export interface PartnerApiAccessDefinition {
  key: 'useRydpayApis' | 'useFleetApis' | 'usesPayByInvoice';
  label: string;
  description: string;
}

export const PARTNER_API_ACCESS_DEFINITIONS: PartnerApiAccessDefinition[] = [
  {
    key: 'useRydpayApis',
    label: 'Use rydpay APIs',
    description: 'Enable access to rydpay payment and transaction APIs.',
  },
  {
    key: 'useFleetApis',
    label: 'Use Fleet APIs and/or ryd-connect',
    description: 'Enable access to Fleet management and driver APIs.',
  },
  {
    key: 'usesPayByInvoice',
    label: 'Pay-by-Invoice Payments',
    description:
      'The partner settles rydpay orders centrally by invoice (postpay). When enabled, activating the ' +
      "pay-by-invoice payment method becomes a mandatory step on the partner's onboarding status page. " +
      "Defaults to on when the partner's onboarding journey selected pay-by-invoice.",
  },
];
